import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, X, Send, Loader2, Paperclip, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

/**
 * Reusable email-send modal for Quotes (Offertes) and Invoices (Facturen).
 *
 * Props:
 *   open          : boolean
 *   onClose       : () => void
 *   documentType  : 'quote' | 'invoice'
 *   document      : the quote or invoice object (must have id, number, client_name, client_email)
 *   pdfNode       : ref.current to the DOM node that should be converted to PDF (e.g. printRef.current)
 *   publicLinkBase: base url for the public quote link (only used when documentType === 'quote')
 *   onSent        : optional callback after success
 */
const EmailSendModal = ({ open, onClose, documentType, document, pdfNode, publicLinkBase, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [signature, setSignature] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [recipient, setRecipient] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfBase64, setPdfBase64] = useState('');
  const [pdfFilename, setPdfFilename] = useState('');
  const [extraFiles, setExtraFiles] = useState([]);
  // Token used for the public link. Starts from document.public_token but is
  // regenerated on the fly when missing (legacy invoices without one) so the
  // {payment_link} placeholder never resolves to an empty href.
  const [effectiveToken, setEffectiveToken] = useState('');

  const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Compute total INCL BTW from document (covers both quotes and invoices)
  const docTotal = () => {
    if (!document) return 0;
    if (document.total != null) return document.total;
    if (document.total_incl != null) return document.total_incl;
    return 0;
  };

  // Compute total EXCL BTW from document — used by {total_excl_btw} placeholder
  // (more professional in NL: clients see net price, BTW is just passed through to tax).
  const docTotalExcl = () => {
    if (!document) return 0;
    if (document.subtotal != null) return Math.max(Number(document.subtotal) - Number(document.general_discount || 0), 0);
    return docTotal();
  };

  // Substitute placeholders in subject/body
  const fillPlaceholders = (tpl) => {
    if (!tpl || !document) return tpl || '';
    const token = effectiveToken || document.public_token || '';
    const publicLink = documentType === 'quote' && token
      ? `${publicLinkBase || window.location.origin}/offerte/${token}`
      : '';
    const paymentLink = documentType === 'invoice' && token
      ? `${publicLinkBase || window.location.origin}/factuur/${token}`
      : '';
    const fmtD = (iso) => { if (!iso) return ''; const s = String(iso).slice(0, 10); const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : s; };
    const parseD = (s) => { if (!s) return null; const a = String(s).slice(0, 10); let m = a.match(/^(\d{4})-(\d{2})-(\d{2})$/); if (m) return new Date(+m[1], +m[2] - 1, +m[3]); m = a.match(/^(\d{2})-(\d{2})-(\d{4})$/); return m ? new Date(+m[3], +m[2] - 1, +m[1]) : null; };
    const openAmount = (() => {
      let paid = 0; const total = docTotal();
      if (document.down_payment?.status === 'paid') paid += Number(document.down_payment.amount_value || 0);
      if (!document.installment_ref) (document.payment_plan?.installments || []).forEach((i) => { if (i.paid_at && Number(i.pct) > 0) paid += Math.round(total * Number(i.pct)) / 100; });
      return Math.max(total - paid, 0);
    })();
    const incasso = (() => {
      let rest = openAmount, k = 0;
      [[2500, 0.15], [2500, 0.10], [5000, 0.05], [190000, 0.01], [Infinity, 0.005]].forEach(([g, p]) => { const d = Math.min(rest, g); if (d > 0) { k += d * p; rest -= d; } });
      return openAmount > 0 ? Math.min(Math.max(k, 40), 6775) : 0;
    })();
    const today = new Date(); const newDue = new Date(today); newDue.setDate(newDue.getDate() + 14);
    const due = parseD(document.due_date);
    const daysOverdue = due ? Math.max(Math.floor((today - due) / 86400000), 0) : 0;
    const pad = (n) => String(n).padStart(2, '0');
    const fmtJs = (d) => `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    return tpl
      .replaceAll('{number}', document.number || '')
      .replaceAll('{client_name}', document.client_name || '')
      // Newer placeholders — substitute BEFORE {total} so they don't get partially matched
      .replaceAll('{total_excl_btw}', fmtEuro(docTotalExcl()))
      .replaceAll('{total_incl_btw}', fmtEuro(docTotal()))
      .replaceAll('{open_amount}', fmtEuro(openAmount))
      .replaceAll('{incasso_kosten}', fmtEuro(incasso))
      .replaceAll('{days_overdue}', String(daysOverdue))
      .replaceAll('{new_due_date}', fmtJs(newDue))
      .replaceAll('{reminder1_date}', fmtD(document.reminders?.reminder1?.sent_at))
      .replaceAll('{reminder2_date}', fmtD(document.reminders?.reminder2?.sent_at))
      .replaceAll('{reminder3_date}', fmtD(document.reminders?.reminder3?.sent_at) || 'eerder')
      .replaceAll('{laatste_datum}', (() => { const d = new Date(today); let n = 5; while (n > 0) { d.setDate(d.getDate() + 1); if (d.getDay() % 6 !== 0) n -= 1; } return fmtJs(d); })())
      .replaceAll('{totaal_met_kosten}', fmtEuro(openAmount + incasso + (due ? Math.round(openAmount * 0.04 * daysOverdue / 365 * 100) / 100 : 0)))
      .replaceAll('{invoice_date}', fmtD(document.invoice_date))
      .replaceAll('{due_date}', fmtD(document.due_date))
      .replaceAll('{total}', fmtEuro(docTotal()))
      .replaceAll('{public_link}', publicLink)
      .replaceAll('{payment_link}', paymentLink)
      .replaceAll('{signature}', signature || '');
  };

  // Fetch templates + signature when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setExtraFiles([]);
    // For quotes, fetch quote + reminders + aangepaste offerte (revised)
    // For invoices, fetch invoice + invoice_reminder_due
    const types = documentType === 'quote'
      ? 'quote,quote_revised,quote_reminder_7d,quote_reminder_expiry,quote_aanbetaling_herinnering'
      : 'invoice,invoice_herinnering_1,invoice_aanmaning,invoice_wik,invoice_incasso_aankondiging,invoice_reminder_due';
    (async () => {
      try {
        const [tplRes, sigRes] = await Promise.all([
          axiosInstance.get(`/email/templates?type=${types}`),
          axiosInstance.get('/email/signature'),
        ]);
        if (cancelled) return;
        setSignature(sigRes.data.signature_html || '');
        setTemplates(tplRes.data || []);
        // Default: prefer the main type (quote/invoice) marked is_default; never auto-pick a reminder
        const all = tplRes.data || [];
        const mainType = documentType === 'quote' ? 'quote' : 'invoice';
        const defaultTpl =
          all.find(t => t.type === mainType && t.is_default) ||
          all.find(t => t.type === mainType) ||
          all[0];
        if (defaultTpl) {
          setSelectedTemplateId(defaultTpl.id);
        }
      } catch (err) {
        toast.error('Fout bij laden van templates');
      }
    })();
    return () => { cancelled = true; };
  }, [open, documentType]);

  // Reset form fields whenever the modal opens for a (possibly different) doc
  useEffect(() => {
    if (!open || !document) return;
    setRecipient(document.client_email || '');
    setCc('');
    setBcc('');
    setPdfReady(false);
    setPdfBase64('');
    setPdfFilename('');
  }, [open, document?.id]);

  // Ensure we have a public_token to build the payment/public link. Legacy
  // invoices created before iter64 might not have one — call the regenerate
  // endpoint silently so {payment_link} never produces href="".
  useEffect(() => {
    if (!open || !document) return;
    let cancelled = false;
    (async () => {
      if (document.public_token) {
        if (!cancelled) setEffectiveToken(document.public_token);
        return;
      }
      if (documentType !== 'invoice' && documentType !== 'quote') return;
      try {
        const endpoint = documentType === 'invoice'
          ? `/invoices/${document.id}/regenerate-public-token`
          : `/quotes/${document.id}/regenerate-public-token`;
        const r = await axiosInstance.post(endpoint);
        if (cancelled) return;
        const tok = r?.data?.public_token || '';
        if (tok) setEffectiveToken(tok);
      } catch (e) {
        // Non-blocking — user will see the email without a link if it fails.
        console.error('regenerate-public-token failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [open, document?.id, document?.public_token, documentType]);

  // Apply template once it's loaded + signature is available
  useEffect(() => {
    if (!selectedTemplateId || !templates.length) return;
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    setSubject(fillPlaceholders(tpl.subject));
    setBodyHtml(fillPlaceholders(tpl.body_html));
  }, [selectedTemplateId, signature, document, templates, effectiveToken]); // eslint-disable-line

  // Generate PDF (as base64) from the provided DOM node
  const generatePdf = async () => {
    if (!pdfNode) {
      toast.error('PDF-voorvertoning niet geladen — open eerst Bekijken');
      return;
    }
    setGenerating(true);
    try {
      const filename = `${documentType === 'quote' ? 'Offerte' : 'Factuur'}_${document.number}_${(document.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      const opts = {
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };
      // html2pdf outputs base64 (no data URI prefix) when using 'datauristring' is not what we want.
      // Use 'output("datauristring")' then strip.
      const dataUri = await html2pdf().set(opts).from(pdfNode).outputPdf('datauristring');
      // dataUri looks like "data:application/pdf;base64,XXXX"
      setPdfBase64(dataUri);
      setPdfFilename(filename);
      setPdfReady(true);
      toast.success('PDF gegenereerd — klaar om te versturen');
    } catch (err) {
      toast.error('Fout bij genereren PDF');
    } finally {
      setGenerating(false);
    }
  };

  const addExtraFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (!files.length) { toast.error('Alleen PDF-bestanden'); return; }
    const read = (f) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res({ filename: f.name, size: f.size, content_base64: r.result });
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const next = await Promise.all(files.map(read));
    pushExtra(next);
  };

  const pushExtra = (next) => {
    const total = [...extraFiles, ...next].reduce((s, f) => s + f.size, 0);
    if (total > 20 * 1024 * 1024) { toast.error('Bijlagen samen max 20MB'); return; }
    setExtraFiles(prev => [...prev, ...next.filter(n => !prev.some(p => p.filename === n.filename))]);
  };

  const [rebranding, setRebranding] = useState(false);
  const rebrandAndAttach = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (!files.length) { toast.error('Alleen PDF-bestanden'); return; }
    setRebranding(true);
    try {
      const out = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axiosInstance.post('/tools/rebrand-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' }, responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const dataUrl = await new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = ko; r.readAsDataURL(blob); });
        out.push({ filename: f.name.replace(/\.pdf$/i, '') + '_Cardoso_Bouw.pdf', size: blob.size, content_base64: dataUrl, rebranded: true });
      }
      pushExtra(out);
      toast.success(`${out.length} PDF omgezet naar Cardoso Bouw en toegevoegd als bijlage`);
    } catch (e) {
      toast.error('Omzetten mislukt');
    } finally {
      setRebranding(false);
    }
  };

  const handleSend = async () => {
    if (!recipient) { toast.error('Vul het e-mailadres in'); return; }
    if (!subject) { toast.error('Vul het onderwerp in'); return; }
    if (!bodyHtml) { toast.error('Vul de inhoud in'); return; }
    if (!pdfReady) { toast.error('Genereer eerst de PDF-bijlage'); return; }
    setSending(true);
    try {
      const payload = {
        document_type: documentType,
        document_id: document.id,
        recipient,
        cc: cc.split(',').map(s => s.trim()).filter(Boolean),
        bcc: bcc.split(',').map(s => s.trim()).filter(Boolean),
        subject,
        body_html: bodyHtml,
        pdf_base64: pdfBase64,
        pdf_filename: pdfFilename,
        extra_attachments: extraFiles.map(f => ({ filename: f.filename, content_base64: f.content_base64 })),
        include_public_link: documentType === 'quote',
        template_type: templates.find(t => t.id === selectedTemplateId)?.type || null,
      };
      const res = await axiosInstance.post('/email/send', payload);
      toast.success(`E-mail verzonden naar ${recipient}`);
      onSent && onSent(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
      data-testid="email-send-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[95vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#3da5a5]" />
            <h2 className="text-lg font-semibold text-gray-800">
              {documentType === 'quote' ? 'Offerte versturen' : 'Factuur versturen'} — {document?.number}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="email-close-btn"><X className="w-4 h-4" /></Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {templates.length > 0 && (
            <div>
              <Label className="text-gray-700 text-xs">Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="email-template-select">
                  <SelectValue placeholder="Kies template..." />
                </SelectTrigger>
                <SelectContent className="z-[10000]" position="popper" sideOffset={4}>
                  {templates.map(t => {
                    const mainType = documentType === 'quote' ? 'quote' : 'invoice';
                    const isReminder = t.type !== mainType && t.type !== 'quote_revised' && t.type !== 'quote_aanbetaling_herinnering';
                    const isRevised = t.type === 'quote_revised';
                    const isAanbetaling = t.type === 'quote_aanbetaling_herinnering';
                    let reminderLabel = '';
                    if (t.type === 'quote_reminder_7d') reminderLabel = '⏰ Herinnering — 7 dagen';
                    else if (t.type === 'quote_reminder_expiry') reminderLabel = '⏰ Herinnering — vervaldatum';
                    else if (t.type === 'invoice_reminder_due') reminderLabel = '⏰ Attendering — vervaldatum';
                    else if (t.type === 'invoice_herinnering_1') reminderLabel = '⏰ Stap 1 — Betalingsherinnering';
                    else if (t.type === 'invoice_aanmaning') reminderLabel = '⚠️ Stap 2 — Aanmaning';
                    else if (t.type === 'invoice_wik') reminderLabel = '🛑 Stap 3 — 14-dagenbrief';
                    else if (t.type === 'invoice_incasso_aankondiging') reminderLabel = '⚖️ Laatste herinnering vóór incasso';
                    return (
                      <SelectItem key={t.id} value={t.id}>
                        {isReminder
                          ? <span className="text-amber-700">{reminderLabel}: {t.name}</span>
                          : isRevised
                            ? <span className="text-emerald-700">📝 Aangepaste offerte (revisie): {t.name}</span>
                            : isAanbetaling
                              ? <span className="text-orange-700">💶 Aanbetaling herinnering: {t.name}</span>
                              : <span>{t.name}{t.is_default ? ' (standaard)' : ''}</span>}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {(() => {
                const sel = templates.find(t => t.id === selectedTemplateId);
                if (!sel) return null;
                if (sel.type === 'quote_revised') {
                  return <p className="text-[11px] text-emerald-700 mt-1 italic">📝 Aangepaste offerte — voor klanten die al een aanbetaling hebben gedaan en aan wie u de bijgewerkte versie stuurt.</p>;
                }
                const mainType = documentType === 'quote' ? 'quote' : 'invoice';
                if (sel.type !== mainType) {
                  return <p className="text-[11px] text-amber-700 mt-1 italic">⚠️ Je hebt een herinnerings-template geselecteerd — handig voor lembrete naar bestaande offertes/facturen.</p>;
                }
                return null;
              })()}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-700 text-xs">Aan *</Label>
              <Input
                type="email"
                value={recipient}
                onChange={e => setRecipient(e.target.value)}
                placeholder="klant@email.com"
                className="bg-white border-gray-200"
                data-testid="email-recipient-input"
              />
            </div>
            <div>
              <Label className="text-gray-700 text-xs">CC (komma-gescheiden)</Label>
              <Input
                value={cc}
                onChange={e => setCc(e.target.value)}
                placeholder="optioneel"
                className="bg-white border-gray-200"
                data-testid="email-cc-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-gray-700 text-xs">BCC (komma-gescheiden)</Label>
            <Input
              value={bcc}
              onChange={e => setBcc(e.target.value)}
              placeholder="optioneel — bijv. info@cardosobouw.nl"
              className="bg-white border-gray-200"
              data-testid="email-bcc-input"
            />
          </div>

          <div>
            <Label className="text-gray-700 text-xs">Onderwerp *</Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="bg-white border-gray-200"
              data-testid="email-subject-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-gray-700 text-xs">Inhoud (HTML toegestaan)</Label>
              <span className="text-[10px] text-gray-400">Beschikbaar: {`{number}, {client_name}, {total_excl_btw}, {total_incl_btw}, {public_link}, {payment_link}, {signature}`}</span>
            </div>
            <textarea
              value={bodyHtml}
              onChange={e => setBodyHtml(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 h-48 font-mono"
              data-testid="email-body-textarea"
            />
          </div>

          {/* Live preview */}
          <details className="bg-gray-50 rounded-lg border border-gray-200 p-3">
            <summary className="text-xs text-gray-600 cursor-pointer font-medium">Voorvertoning e-mail</summary>
            <div
              className="mt-3 bg-white border border-gray-200 rounded p-3 text-sm prose max-w-none"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          </details>

          {/* PDF attachment */}
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div className="flex items-center gap-2 text-sm">
              <Paperclip className="w-4 h-4 text-blue-600" />
              {pdfReady ? (
                <span className="text-blue-700 font-medium" data-testid="email-pdf-status">
                  PDF klaar: {pdfFilename}
                </span>
              ) : (
                <span className="text-gray-600">Nog geen PDF-bijlage</span>
              )}
            </div>
            <Button
              size="sm"
              onClick={generatePdf}
              disabled={generating || !pdfNode}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="email-generate-pdf-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : (pdfReady ? 'Opnieuw genereren' : 'PDF genereren')}
            </Button>
          </div>

          {/* Extra PDF attachments */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2" data-testid="email-extra-attachments">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Paperclip className="w-4 h-4 text-gray-500" />
                <span className="font-medium whitespace-nowrap">Extra bijlagen (PDF)</span>
              </div>
              <div className="flex items-center gap-3 whitespace-nowrap">
                <label className="cursor-pointer text-sm font-medium text-gray-700 hover:underline flex items-center gap-1" data-testid="email-rebrand-attach-label" title="Leveranciers-PDF omzetten naar Cardoso Bouw en direct bijvoegen">
                  {rebranding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Leveranciers-PDF op eigen naam
                  <input type="file" accept="application/pdf,.pdf" multiple className="hidden" disabled={rebranding}
                    onChange={e => { rebrandAndAttach(e.target.files); e.target.value = ''; }}
                    data-testid="email-rebrand-attach-input" />
                </label>
                <label className="cursor-pointer text-sm font-medium text-[#2d8585] hover:underline" data-testid="email-extra-attachments-label">
                  + PDF toevoegen
                  <input type="file" accept="application/pdf,.pdf" multiple className="hidden"
                    onChange={e => { addExtraFiles(e.target.files); e.target.value = ''; }}
                    data-testid="email-extra-attachments-input" />
                </label>
              </div>
            </div>
            {extraFiles.length > 0 && (
              <ul className="space-y-1" data-testid="email-extra-attachments-list">
                {extraFiles.map(f => (
                  <li key={f.filename} className="flex items-center justify-between text-sm bg-white border border-gray-200 rounded px-2 py-1">
                    <span className="truncate text-gray-800 flex items-center gap-1">{f.rebranded && <Wand2 className="w-3 h-3 text-[#2d8585]" />}{f.filename} <span className="text-gray-400 text-xs">({(f.size / 1024).toFixed(0)} kB)</span></span>
                    <button type="button" onClick={() => setExtraFiles(extraFiles.filter(x => x.filename !== f.filename))}
                      className="text-gray-400 hover:text-red-600" data-testid={`email-extra-remove-${f.filename}`}><X className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="email-cancel-btn">Annuleren</Button>
          <Button
            onClick={handleSend}
            disabled={sending || !pdfReady}
            className="bg-[#3da5a5] hover:bg-[#2d8585]"
            data-testid="email-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Versturen
          </Button>
        </div>
      </div>
    </div>,
    window.document.body
  );
};

export default EmailSendModal;
